// Récupération d'un compte : l'usage effectif des parts Shamir.
//
// **La contrainte qui dicte tout** : l'avocat a oublié son mot de passe. Il n'a
// donc plus aucune clé, et personne — pas même ses dépositaires — ne peut rien
// lui chiffrer. Il fabrique pour l'occasion une paire X25519 **éphémère**, qui ne
// vit que le temps de la récupération.
//
//   1. Il ouvre une demande et publie sa clé publique éphémère.
//   2. Chaque dépositaire descelle sa part (avec sa clé dérivée de SA clé
//      maîtresse) et la **re-scelle** à cette clé éphémère.
//   3. Deux parts suffisent : il les descelle, recombine K, et prouve au serveur
//      qu'il l'a reconstituée — sans la lui révéler.
//   4. Il pose un nouveau mot de passe, et ré-enveloppe **la même** K.
//
// Le serveur ne relaie que des blobs qu'il ne peut pas ouvrir. Et **ce n'est pas
// lui qui authentifie le demandeur** : il en est incapable. Ce sont les
// dépositaires, hors bande — au téléphone, de visu — en comparant l'empreinte.

import * as opaque from '@serenity-kit/opaque';
import { api } from '../api/client';
import {
  combineShares,
  deriveKek,
  deriveSubkey,
  loadCrypto,
  proveReconstitution,
  sealShare,
  unsealShare,
  wrapMasterKey,
  x25519PublicKey,
} from './index';

const toBase64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const fromBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
const exportKeyBytes = (exportKey: string) => new TextEncoder().encode(exportKey);

/**
 * Clé de dépositaire — **dérivée** de la clé maîtresse, jamais stockée.
 * Identique à celle de `recovery.ts` : c'est la même clé, recalculée.
 */
const recoveryPrivateKey = (masterKey: Uint8Array) =>
  deriveSubkey(masterKey, 'recovery-x25519');

/**
 * Empreinte lisible d'une clé publique — celle qu'on épelle au téléphone.
 *
 * SHA-256, **exactement comme le serveur la calcule** : le demandeur l'affiche
 * depuis sa propre clé, le dépositaire depuis celle que le serveur lui a
 * transmise. Si les deux concordent, c'est que le serveur n'a pas substitué la
 * clé — et c'est bien à ce confrère-là que la part va être re-scellée.
 */
export async function fingerprintOf(publicKey: Uint8Array): Promise<string> {
  // Copie dans un buffer que WebCrypto accepte : les Uint8Array rendus par le
  // wasm ne portent pas le type d'ArrayBuffer qu'attend `digest`.
  const bytes = new Uint8Array(publicKey.length);
  bytes.set(publicKey);

  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return Array.from(digest.slice(0, 8))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}

/**
 * La demande en cours, telle que le navigateur du demandeur la garde.
 *
 * Elle est stockée **en clair** dans le `localStorage`, et ce n'est pas un
 * oubli : celui qui la détient n'a plus aucune clé — c'est précisément son
 * problème. Il n'y a rien avec quoi la chiffrer.
 *
 * Le risque reste borné : la clé éphémère seule ne donne rien. Il faut que **deux
 * dépositaires** aient libéré leur part, ce qu'ils ne font qu'après avoir
 * authentifié le demandeur au téléphone. Et la demande meurt avec la
 * réinitialisation.
 */
export type PendingRecovery = {
  requestId: string;
  claimToken: string;
  /** Clé privée X25519 éphémère, base64. */
  ephemeralPrivateKey: string;
  email: string;
  tenantSlug: string | null;
};

const STORAGE_KEY = 'delo.recovery.pending';

export function loadPendingRecovery(): PendingRecovery | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PendingRecovery;
  } catch {
    return null;
  }
}

export function clearPendingRecovery(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/** L'empreinte à épeler, recalculée depuis la clé conservée (après rechargement). */
export async function pendingFingerprint(
  pending: PendingRecovery,
): Promise<string> {
  await loadCrypto();
  return fingerprintOf(x25519PublicKey(fromBase64(pending.ephemeralPrivateKey)));
}

// ---------------------------------------------------------------------------
// Demandeur (sans session — il n'en a précisément plus)
// ---------------------------------------------------------------------------

/**
 * Ouvre une demande de récupération.
 *
 * Elle ne donne aucun pouvoir en soi : tout dépend ensuite des dépositaires, qui
 * décident après avoir vérifié qui demande.
 */
export async function openRecovery(
  tenantSlug: string | null,
  email: string,
): Promise<{ pending: PendingRecovery; fingerprint: string }> {
  await loadCrypto();

  const privateKey = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = x25519PublicKey(privateKey);

  const { requestId, claimToken } = await api<{
    requestId: string;
    claimToken: string;
  }>('/recovery/requests', {
    method: 'POST',
    tenantSlug,
    body: {
      email,
      requesterPublicKey: toBase64(publicKey),
      reason: 'lost_credentials',
    },
  });

  const pending: PendingRecovery = {
    requestId,
    claimToken,
    ephemeralPrivateKey: toBase64(privateKey),
    email,
    tenantSlug,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));

  return { pending, fingerprint: await fingerprintOf(publicKey) };
}

export type RecoveryStatus = {
  id: string;
  status: string;
  threshold: number;
  releases: { shareIndex: number; resealedBlob: string }[];
  challenge: string;
  expiresAt: string;
};

export function recoveryStatus(pending: PendingRecovery): Promise<RecoveryStatus> {
  return api<RecoveryStatus>(`/recovery/requests/${pending.requestId}/status`, {
    method: 'POST',
    tenantSlug: pending.tenantSlug,
    body: { claimToken: pending.claimToken },
  });
}

/** Descelle les parts que les dépositaires ont libérées à ma clé éphémère. */
export async function unsealReleases(
  pending: PendingRecovery,
  status: RecoveryStatus,
): Promise<Uint8Array[]> {
  await loadCrypto();
  const privateKey = fromBase64(pending.ephemeralPrivateKey);
  return status.releases.map((r) =>
    unsealShare(fromBase64(r.resealedBlob), privateKey),
  );
}

/**
 * Le dénouement : recombine K, prouve au serveur qu'on l'a reconstituée, et pose
 * un nouveau mot de passe.
 *
 * K est **la même clé** qu'avant, simplement remise sous une nouvelle enveloppe :
 * dossiers, documents et parts Shamir déjà déposées restent valides. C'est tout
 * l'intérêt d'une clé maîtresse indépendante du mot de passe.
 */
export async function completeRecovery(
  pending: PendingRecovery,
  status: RecoveryStatus,
  parts: Uint8Array[],
  newPassword: string,
): Promise<{ twoFactorDisabled: boolean }> {
  await loadCrypto();
  await opaque.ready;

  const masterKey = combineShares(parts) as Uint8Array;

  // La preuve de reconstitution : signature du défi par K, vérifiable contre
  // l'empreinte publique que le serveur détient. C'est ELLE qui autorise la
  // réinitialisation — ni une session (il n'y en a pas), ni un mot de passe (il
  // est oublié). Et si les parts étaient mauvaises, le serveur refuse ICI, avant
  // d'avoir touché au compte.
  const proof = proveReconstitution(masterKey, fromBase64(status.challenge));

  const { clientRegistrationState, registrationRequest } =
    opaque.client.startRegistration({ password: newPassword });

  const started = await api<{ registrationResponse: string; resetToken: string }>(
    `/recovery/requests/${pending.requestId}/reset/start`,
    {
      method: 'POST',
      tenantSlug: pending.tenantSlug,
      body: {
        claimToken: pending.claimToken,
        challenge: status.challenge,
        proof: toBase64(proof),
        registrationRequest,
      },
    },
  );

  const { registrationRecord, exportKey } = opaque.client.finishRegistration({
    clientRegistrationState,
    registrationResponse: started.registrationResponse,
    password: newPassword,
  });

  // Le nouveau verifier ET la clé ré-enveloppée partent ensemble : le serveur les
  // écrit dans la même transaction. Les dissocier laisserait un compte au mot de
  // passe neuf mais aux données définitivement illisibles.
  // Le serveur lève aussi le second facteur : celui qui a perdu son mot de passe
  // a très bien pu perdre son téléphone dans le même sinistre, et buterait alors
  // sur un code TOTP qu'il ne peut plus produire — enfermé dehors malgré une
  // récupération réussie. Il devra le remettre en place ; on le lui dira.
  const done = await api<{ ok: true; twoFactorDisabled: boolean }>(
    '/recovery/reset/finish',
    {
      method: 'POST',
      tenantSlug: pending.tenantSlug,
      body: {
        resetToken: started.resetToken,
        registrationRecord,
        encryptedMasterKey: toBase64(
          wrapMasterKey(deriveKek(exportKeyBytes(exportKey)), masterKey),
        ),
      },
    },
  );

  clearPendingRecovery();
  return { twoFactorDisabled: done.twoFactorDisabled };
}

// ---------------------------------------------------------------------------
// Dépositaire (authentifié)
// ---------------------------------------------------------------------------

export type PendingRequest = {
  requestId: string;
  shareId: string;
  ownerEmail: string;
  reason: string;
  /** ⚠️ À COMPARER HORS BANDE avec ce que le demandeur affiche sur son écran. */
  requesterFingerprint: string;
  sealedShareBlob: string;
  requesterPublicKey: string;
  alreadyReleased: boolean;
  createdAt: string;
};

export function listPendingRequests(token: string): Promise<PendingRequest[]> {
  return api<PendingRequest[]>('/recovery/pending', { token });
}

/**
 * Libère ma part : je la descelle avec ma clé, je la re-scelle à la clé éphémère
 * du demandeur.
 *
 * ⚠️ **Le serveur ne peut pas savoir qui demande.** Il ne voit qu'une adresse
 * e-mail et une clé publique — un imposteur qui connaît l'adresse de mon confrère
 * produirait exactement la même chose. C'est à MOI de l'appeler et de comparer
 * l'empreinte. Sans cette vérification, toute la mécanique ne vaut rien.
 */
export async function releaseShare(
  token: string,
  masterKey: Uint8Array,
  request: PendingRequest,
): Promise<void> {
  await loadCrypto();

  const share = unsealShare(
    fromBase64(request.sealedShareBlob),
    recoveryPrivateKey(masterKey),
  );
  const resealed = sealShare(share, fromBase64(request.requesterPublicKey));

  await api(
    `/recovery/requests/${request.requestId}/shares/${request.shareId}/release`,
    { method: 'POST', token, body: { resealedBlob: toBase64(resealed) } },
  );
}
